import { gql } from '@apollo/client';
import {
  DatePickerFieldsFragment,
  LogicalOperatorFragment,
  MeasureFunctionFragment,
  RelationalOperatorFragment,
  SubjectFragment,
} from '../fragments';
import {
  ExpressionCohortOperatorFragment,
  FunnelCohortOperatorFragment,
  PerformedCohortOperatorFragment,
  MeasureCohortOperatorFragment,
  RetentionCohortOperatorFragment,
  SimpleCohortTypeFragment,
  TimeWindowFragment,
  TimeToConvertCohortConfigFragment,
} from './fragments';
import { CohortSampleOperationFragment } from './sample';

const ExpressionCohortConfigFragment = gql`
  fragment ExpressionCohortConfigFields on ExpressionCohortConfig {
    operators {
      ...ExpressionCohortOperatorFields
    }
    timeWindows {
      ...TimeWindowFields
    }
    defaultTimeWindow {
      ...TimeWindowFields
    }
    subjects {
      ...SubjectFields
    }
    defaultSubject {
      ...SubjectFields
    }
    percentileOperators {
      ...RelationalOperatorFields
    }
  }
  ${ExpressionCohortOperatorFragment}
  ${TimeWindowFragment}
  ${SubjectFragment}
  ${RelationalOperatorFragment}
`;

const FunnelCohortConfigFragment = gql`
  fragment FunnelCohortConfigFields on FunnelCohortConfig {
    operators {
      ...FunnelCohortOperatorFields
    }
    timeWindows {
      ...TimeWindowFields
    }
    defaultTimeWindow {
      ...TimeWindowFields
    }
    subjects {
      ...SubjectFields
    }
    defaultSubject {
      ...SubjectFields
    }
    ttcConfig {
      ...TimeToConvertCohortConfigFields
    }
  }
  ${TimeWindowFragment}
  ${FunnelCohortOperatorFragment}
  ${SubjectFragment}
  ${TimeToConvertCohortConfigFragment}
`;

const MeasureCohortConfigFragment = gql`
  fragment MeasureCohortConfigFields on MeasureCohortConfig {
    operators {
      ...MeasureCohortOperatorFields
    }
    timeWindows {
      ...TimeWindowFields
    }
    functions {
      ...MeasureFunctionFields
    }
    defaultTimeWindow {
      ...TimeWindowFields
    }
    subjects {
      ...SubjectFields
    }
    defaultSubject {
      ...SubjectFields
    }
    percentileOperators {
      ...RelationalOperatorFields
    }
  }
  ${MeasureCohortOperatorFragment}
  ${TimeWindowFragment}
  ${MeasureFunctionFragment}
  ${SubjectFragment}
  ${RelationalOperatorFragment}
`;

const PerformedCohortConfigFragment = gql`
  fragment PerformedCohortConfigFields on PerformedCohortConfig {
    operators {
      ...PerformedCohortOperatorFields
    }
    timeWindows {
      ...TimeWindowFields
    }
    defaultTimeWindow {
      ...TimeWindowFields
    }
    subjects {
      ...SubjectFields
    }
    defaultSubject {
      ...SubjectFields
    }
  }
  ${PerformedCohortOperatorFragment}
  ${TimeWindowFragment}
  ${SubjectFragment}
`;

const RetentionCohortConfigFragment = gql`
  fragment RetentionCohortConfigFields on RetentionCohortConfig {
    operators {
      ...RetentionCohortOperatorFields
    }
    returnedOperators {
      ...RelationalOperatorFields
    }
    timeWindows {
      ...TimeWindowFields
    }
    defaultTimeWindow {
      ...TimeWindowFields
    }
    subjects {
      ...SubjectFields
    }
    defaultSubject {
      ...SubjectFields
    }
  }
  ${RelationalOperatorFragment}
  ${TimeWindowFragment}
  ${RetentionCohortOperatorFragment}
  ${SubjectFragment}
`;

export const CohortConfigFragment = gql`
  fragment CohortConfigFields on CohortConfig {
    defaultSamplingRate
    logicOps {
      ...LogicalOperatorFields
    }
    simpleCohortTypes {
      ...SimpleCohortTypeFields
    }
    performedConfig {
      ...PerformedCohortConfigFields
    }
    measureConfig {
      ...MeasureCohortConfigFields
    }
    expressionConfig {
      ...ExpressionCohortConfigFields
    }
    funnelConfig {
      ...FunnelCohortConfigFields
    }
    retentionConfig {
      ...RetentionCohortConfigFields
    }
  }
  ${SubjectFragment}
  ${LogicalOperatorFragment}
  ${SimpleCohortTypeFragment}
  ${PerformedCohortConfigFragment}
  ${MeasureCohortConfigFragment}
  ${ExpressionCohortConfigFragment}
  ${FunnelCohortConfigFragment}
  ${RetentionCohortConfigFragment}
`;

export const CohortExecuteConfigFragment = gql`
  fragment CohortExecuteConfigFields on CohortExecuteConfig {
    defaultStart
    defaultEnd
    timezone
    liveStartDate
    cohortConfig {
      ...CohortConfigFields
    }
    operations {
      ...CohortSampleOperationFields
    }
    defaultOperation {
      ...CohortSampleOperationFields
    }
    defaultDatePicker {
      ...DatePickerFields
    }
  }
  ${CohortConfigFragment}
  ${CohortSampleOperationFragment}
  ${DatePickerFieldsFragment}
`;

export const GET_COHORT_EXECUTE_CONFIG = gql`
  query CohortExecuteConfig($schemaId: ID!) {
    cohortExecuteConfig(schemaId: $schemaId) {
      ...CohortExecuteConfigFields
    }
  }
  ${CohortExecuteConfigFragment}
`;
